import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { setDraftMode } from "@/lib/speak";

import TutorialPlayer from "@/pages/lessons/TutorialPlayer";
import PhonicsEngine from "@/components/engines/PhonicsEngine";
import McqEngine from "@/components/engines/McqEngine";
import CountEngine from "@/components/engines/CountEngine";
import SinkFloatEngine from "@/components/engines/SinkFloatEngine";
import MatchEngine from "@/components/engines/MatchEngine";
import TraceEngine from "@/components/engines/TraceEngine";

const ENGINES = { phonics: PhonicsEngine, mcq: McqEngine, count: CountEngine, sink_float: SinkFloatEngine, match: MatchEngine, trace: TraceEngine };

/**
 * Preview a lesson without saving progress.
 * Pass either `lessonId` (fetches from API) or `draft` ({title, activities}).
 */
export default function LessonPreview({ open, onClose, lessonId, draft, color = "#0D9488" }) {
  const [lesson, setLesson] = useState(null);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [phase, setPhase] = useState("learn");

  useEffect(() => {
    if (!open) { setDraftMode(false); return; }
    setStep(0); setDone(false); setPhase("learn");
    setDraftMode(!!draft);
    if (draft) { setLesson({ title: draft.title, tutorial: draft.tutorial || { steps: [] }, activities: draft.activities.map((a, i) => ({ ...a, id: a.id || `preview_${i}` })) }); return; }
    if (lessonId) {
      setLesson(null);
      api.getLesson(lessonId).then((l) => setLesson({ ...l, activities: (l.activities || []).map((a, i) => ({ ...a, id: a.id || `preview_${i}` })) })).catch(() => setLesson(false));
    }
  }, [open, lessonId, draft]);

  useEffect(() => () => setDraftMode(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const activities = lesson?.activities || [];
  const hasTutorial = (lesson?.tutorial?.steps || []).length > 0;
  const activity = activities[step];
  const Engine = activity ? ENGINES[activity.engine] : null;

  const advance = () => {
    if (step + 1 >= activities.length) setDone(true);
    else setStep(step + 1);
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-[#1F2937]/70 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="lesson-preview">
        <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }}
          className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-[#E5DECF] px-5 py-3.5">
            <div className="flex items-center gap-2">
              <span className="rounded-full px-2.5 py-1 text-xs font-bold text-white" style={{ background: color }}>Preview</span>
              <span className="font-heading font-bold text-[#1F2937]">{lesson?.title || "Lesson"}</span>
            </div>
            <button onClick={onClose} data-testid="close-preview" className="rounded-xl p-1.5 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={20} /></button>
          </div>

          <div className="overflow-y-auto p-6">
            {lesson === false && <p className="py-12 text-center font-heading text-[#6B7280]">Lesson not found.</p>}
            {lesson === null && <p className="py-12 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading…</p>}

            {lesson && hasTutorial && phase === "learn" && !done && (
              <TutorialPlayer tutorial={lesson.tutorial} color={color} subjectName={lesson.subject?.name} onDone={() => setPhase("practice")} />
            )}

            {lesson && !(hasTutorial && phase === "learn") && !done && (
              <>
                <div className="mb-5 flex items-center gap-2">
                  {activities.map((a, i) => (
                    <div key={a.id} className="h-2 flex-1 overflow-hidden rounded-full bg-[#F2ECE1]">
                      <div className="h-full rounded-full transition-all" style={{ width: i <= step ? "100%" : "0%", background: color }} />
                    </div>
                  ))}
                </div>
                <div className="mb-4 text-center">
                  <div className="font-body text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Step {step + 1} of {activities.length}</div>
                  <h2 className="mt-1 font-heading text-2xl font-bold text-[#1F2937]">{activity?.title}</h2>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div key={activity.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.12 } }}>
                    {Engine ? (
                      <Engine key={activity.id} activity={activity} onComplete={advance} />
                    ) : (
                      <div className="text-center">
                        <p className="text-[#6B7280]">This activity type can't be previewed.</p>
                        <button onClick={advance} className="mt-4 rounded-full bg-[#0D9488] px-6 py-2 font-bold text-white">Skip</button>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </>
            )}

            {lesson && done && (
              <div className="flex flex-col items-center py-10 text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg" style={{ background: color }}><Trophy size={40} /></div>
                <h2 className="mt-5 font-heading text-2xl font-extrabold text-[#1F2937]">Preview complete!</h2>
                <p className="mt-1 font-body text-[#6B7280]">This is exactly what a student will play.</p>
                <div className="mt-6 flex gap-3">
                  <button onClick={() => { setStep(0); setDone(false); }} className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] bg-white px-5 py-2.5 font-bold text-[#1F2937] hover:bg-[#F2ECE1]"><ArrowLeft size={16} /> Replay</button>
                  <button onClick={onClose} className="rounded-full px-5 py-2.5 font-bold text-white" style={{ background: color }}>Close</button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
